import moment from 'moment'
import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import Text from 'components/common/Text'
import { ActiveAttacksList } from 'components/navigation/Notifications/ActiveAttacksList'
import { ScheduledAttackRow } from 'components/navigation/Notifications/AttackRows/ScheduledAttackRow'
import { ScheduledScenarioRow } from 'components/navigation/Notifications/AttackRows/ScheduledScenarioRow'
import { CompletedScenarioList } from 'components/navigation/Notifications/CompletedScenarioList'
import { NewCompletedScenariosNotifications } from 'components/navigation/Notifications/NewCompletedScenariosNotifications'
import { NotificationsDropdown } from 'components/navigation/Notifications/NotificationsDropdown'
import { ActiveNotificationListSection } from 'components/navigation/Notifications/NotificationsList/ActiveNotificationListSection'
import { CompletedScenarioListItem } from 'components/navigation/Notifications/NotificationsList/ListItems/CompletedScenarioListItem'
import { ScheduledAttackListItem } from 'components/navigation/Notifications/NotificationsList/ListItems/ScheduledAttackListItem'
import { ScheduledScenarioDetailsProvider } from 'components/navigation/Notifications/NotificationsList/ListItems/ScheduledScenario/ScheduledScenarioDetailsProvider'
import { ScheduledScenarioListItem } from 'components/navigation/Notifications/NotificationsList/ListItems/ScheduledScenario/ScheduledScenarioListItem'
import { NotificationsList } from 'components/navigation/Notifications/NotificationsList/NotificationsList'
import { NotificationsListItem } from 'components/navigation/Notifications/NotificationsList/NotificationsListItem'
import { NotificationsListSection } from 'components/navigation/Notifications/NotificationsList/NotificationsListSection'
import { willTriggerRunWithin } from 'components/schedules/trigger-utils'
import useAgent from 'hooks/use-agent'
import useAttackResource from 'hooks/use-attack-resource'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { useScenarioResource } from 'hooks/use-scenario-resource'
import { useScheduleResource } from 'hooks/use-schedule-resource'
import { NotificationResponse } from 'types/api/reliability-tests/notifications'
import { ScenarioRun } from 'types/api/scenarios/runs/scenario-runs'

import ListContainer, { MenuItem } from './ListContainer'

function isScenario(schedule) {
  return 'scenario_id' in schedule
}

function willRunInNext12Hours(schedule) {
  return willTriggerRunWithin(
    schedule.trigger,
    moment(),
    moment().add(12, 'hours')
  )
}

const ListOfActiveAttacks: React.FC<{
  showHaltButton?: boolean
  clickableRows?: boolean
  onRowSelected?(): void
  selectableRows?: boolean
  selectedRows?: Record<string, any>
}> = ({
  showHaltButton = true,
  clickableRows = true,
  onRowSelected = () => {},
  selectableRows = false,
  selectedRows = {}
}) => {
  const {
    data: [alfiApplicationQueryMetadata, alfiTrafficQueryMetadata]
  } = useAgent(
    ['/alfi/metadata/applicationQuery', '/alfi/metadata/trafficQuery'],
    [[], []],
    {
      shouldCache: true
    }
  )

  const {
    active: activeAttacks,
    doFetch: doFetchAttacks,
    activeScenarios,
    doFetchActiveScenarios,
    activeApplicationAttacks,
    doFetchApplicationAttacks
  } = useAttackResource({
    completed: false
  })

  return (
    <ActiveAttacksList
      alfiAttacks={activeApplicationAttacks}
      alfiMetadata={{
        applicationQuery: alfiApplicationQueryMetadata,
        trafficQuery: alfiTrafficQueryMetadata
      }}
      clickableRows={clickableRows}
      doFetchAlfiAttacks={doFetchApplicationAttacks}
      doFetchIlfiAttacks={doFetchAttacks}
      doFetchScenarios={doFetchActiveScenarios}
      ilfiAttacks={activeAttacks}
      scenarios={activeScenarios}
      selectableRows={selectableRows}
      selectedRows={selectedRows}
      showHaltButton={showHaltButton}
      onRowSelected={onRowSelected}
    />
  )
}

const ListOfCompletedScenarios: React.FC<{
  doFetchCompletedRuns(): void
  completedScenariosAwaitingResults: ScenarioRun[]
  closePopover(): void
}> = ({
  doFetchCompletedRuns,
  completedScenariosAwaitingResults,
  closePopover
}) => {
  return (
    <CompletedScenarioList
      clickableRows={true}
      closePopover={closePopover}
      completedScenariosAwaitingResults={completedScenariosAwaitingResults}
      doFetchCompletedRuns={doFetchCompletedRuns}
      selectableRows={false}
      showHaltButton={false}
    />
  )
}

export const NotificationsMenu: React.FC = () => {
  const location = useLocation()
  const { hasReliabilityManagement, hasFaultInjection } = useAuth()

  // Fault Injection requests
  const { activeAttacksCount } = useAttackResource({
    completed: false,
    enabled: hasFaultInjection
  })
  const {
    newCompletedScenarios,
    setNewCompletedScenarios,
    completedScenariosAwaitingResults,
    doFetchCompletedRuns,
    isLoading: isLoadingCompletedScenarios,
    hasError: hasErrorCompletedScenarios
  } = useScenarioResource({
    enabled: hasFaultInjection
  })
  const { schedules } = useScheduleResource({
    interval: 20_000,
    enabled: hasFaultInjection
  })
  const scheduledInNext12Hours = schedules.filter(willRunInNext12Hours)
  const hasSchedules = scheduledInNext12Hours.length > 0
  // show scenarios completed within the last 2 weeks, which have no results
  const currentlyOnScenariosRunsPage = location?.pathname.includes('/runs/')
  const scenariosToDisplay = useMemo(
    () => completedScenariosAwaitingResults.slice(0, 10),
    [completedScenariosAwaitingResults]
  )

  // Reliability Management requests
  const {
    data: notifications,
    isFetching: isLoadingNotifications,
    isError: hasErrorNotifications
  } = useGremlinQuery<NotificationResponse | null>(
    'reliability-tests/notifications',
    {},
    {
      enabled: hasReliabilityManagement,
      placeholderData: null,
      refetchInterval: 10_000
    }
  )

  return (
    <>
      <NotificationsDropdown
        activeAttacksCount={activeAttacksCount}
        hasSchedules={hasSchedules}
        pendingResultsScenariosCount={scenariosToDisplay?.length}
      >
        {({ closePopover, isOpen }) => {
          return (
            <>
              {hasReliabilityManagement && (
                <NotificationsList>
                  {activeAttacksCount > 0 && (
                    <ActiveNotificationListSection shouldCollapse={!isOpen} />
                  )}

                  <NotificationsListSection
                    hasError={hasErrorNotifications}
                    isLoading={isLoadingNotifications}
                    sectionIcon="chartNetwork"
                    sectionName="reliability"
                    sectionSubTitle={notifications?.subheading}
                    sectionTitle={
                      notifications?.title ?? 'Reliability Management'
                    }
                    shouldCollapse={!isOpen}
                  >
                    {notifications?.notifications?.map(
                      (notification, index) => (
                        <NotificationsListItem
                          key={`${index}-${notification.text}`} // no unique id here, so this is probably as close as we can get to a unique value
                          notification={notification}
                        />
                      )
                    )}
                  </NotificationsListSection>

                  {scenariosToDisplay.length > 0 && (
                    <NotificationsListSection
                      hasError={hasErrorCompletedScenarios}
                      isLoading={isLoadingCompletedScenarios}
                      sectionIcon="clockRotateLeft"
                      sectionName="completed"
                      sectionSubTitle="Fault Injections in the past 24 hours"
                      sectionTitle={`Recently Completed (${scenariosToDisplay.length})`}
                      shouldCollapse={!isOpen}
                    >
                      {scenariosToDisplay?.map((scenario, index) => (
                        <CompletedScenarioListItem
                          key={`${index}-${scenario.guid}-${scenario.runNumber}`}
                          doFetch={doFetchCompletedRuns}
                          scenario={scenario}
                        />
                      ))}
                    </NotificationsListSection>
                  )}

                  {hasSchedules && (
                    <NotificationsListSection
                      sectionIcon="calendar"
                      sectionName="scheduled"
                      sectionSubTitle="Fault Injections in the next 12 hours"
                      sectionTitle={`Scheduled (${scheduledInNext12Hours.length})`}
                      shouldCollapse={!isOpen}
                    >
                      {scheduledInNext12Hours?.map((schedule) =>
                        isScenario(schedule) ? (
                          <ScheduledScenarioDetailsProvider
                            key={schedule.guid}
                            scenarioId={schedule.scenario_id}
                          >
                            {(scenario, isLoading, hasError) =>
                              isLoading || hasError ? null : (
                                <ScheduledScenarioListItem
                                  scenario={scenario}
                                  schedule={schedule}
                                />
                              )
                            }
                          </ScheduledScenarioDetailsProvider>
                        ) : (
                          <ScheduledAttackListItem
                            key={schedule.guid}
                            schedule={schedule}
                          />
                        )
                      )}
                    </NotificationsListSection>
                  )}
                </NotificationsList>
              )}

              {hasFaultInjection && !hasReliabilityManagement && (
                <ListContainer
                  isEmpty={
                    activeAttacksCount === 0 &&
                    scenariosToDisplay.length === 0 &&
                    !hasSchedules
                  }
                >
                  {activeAttacksCount > 0 && (
                    <MenuItem headerName={`Active (${activeAttacksCount})`}>
                      <ListOfActiveAttacks />
                    </MenuItem>
                  )}
                  {scenariosToDisplay.length > 0 && (
                    <MenuItem
                      headerName={
                        <Text as="span">
                          Recently Completed
                          <Text
                            as="small"
                            css={{ display: 'inline' }}
                            weight="normal"
                          >
                            {' '}
                            in the last 24hrs
                          </Text>
                        </Text>
                      }
                    >
                      <ListOfCompletedScenarios
                        closePopover={closePopover}
                        completedScenariosAwaitingResults={scenariosToDisplay}
                        doFetchCompletedRuns={doFetchCompletedRuns}
                      />
                    </MenuItem>
                  )}
                  {hasSchedules && (
                    <MenuItem
                      headerName={
                        <Text as="span">
                          Scheduled
                          <Text
                            as="small"
                            css={{ display: 'inline' }}
                            weight="normal"
                          >
                            {' '}
                            in the next 12hrs
                          </Text>
                        </Text>
                      }
                    >
                      {scheduledInNext12Hours.map((schedule) =>
                        isScenario(schedule) ? (
                          <ScheduledScenarioRow
                            key={schedule.guid}
                            schedule={schedule}
                          />
                        ) : (
                          <ScheduledAttackRow
                            key={schedule.guid}
                            schedule={schedule}
                          />
                        )
                      )}
                    </MenuItem>
                  )}
                </ListContainer>
              )}
            </>
          )
        }}
      </NotificationsDropdown>
      {hasFaultInjection && currentlyOnScenariosRunsPage && (
        <NewCompletedScenariosNotifications
          newCompletedScenarios={newCompletedScenarios}
          setNewCompletedScenarios={setNewCompletedScenarios}
        />
      )}
    </>
  )
}
